/**
 * Auth utilities — single source of truth for token lifecycle.
 * Fixes the "returning-user glitch": stale JWTs in localStorage no longer trigger
 * a partially-logged-in state where every API call 401s.
 */
import axios from 'axios';

const TOKEN_KEY = 'academy_token';
const USER_KEY  = 'academy_user';

/** Decode a JWT payload without verifying the signature. Returns null on any error. */
const decodeJwt = (token) => {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(normalized + '='.repeat((4 - normalized.length % 4) % 4));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

/** Returns true if the token is missing or expired (exp claim is in the past). */
export const isTokenExpired = (token) => {
  if (!token) return true;
  const payload = decodeJwt(token);
  if (!payload || typeof payload.exp !== 'number') return false; // no exp → treat as valid, backend will reject if bad
  // 30-second skew tolerance
  return payload.exp * 1000 < Date.now() - 30_000;
};

/** Clears all auth-related localStorage keys. Idempotent. */
export const clearAuth = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    // Broadcast to other tabs / Header listeners
    window.dispatchEvent(new StorageEvent('storage', { key: TOKEN_KEY }));
  } catch { /* ignore */ }
};

/** Log the user out and send them to the SAA homepage. */
export const logout = (redirect = '/academy') => {
  clearAuth();
  window.location.href = redirect;
};

/**
 * Called once at app boot. If a stale/expired token is sitting in localStorage,
 * wipe it BEFORE any component renders — prevents the "half-authed glitch".
 */
export const purgeStaleAuthOnBoot = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && isTokenExpired(token)) {
    clearAuth();
    return true;
  }
  return false;
};

/**
 * Attach a global axios interceptor so ANY 401 from the backend
 * (expired token, revoked session, etc.) instantly clears auth state.
 * ALSO attaches the Bearer token on every outgoing request when present,
 * so protected admin endpoints work without every caller adding headers.
 * Called once from index.js.
 */
export const installAuthInterceptor = () => {
  // Request: attach Authorization header when a token is present
  axios.interceptors.request.use((config) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token && !config.headers?.Authorization) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch { /* ignore */ }
    return config;
  });

  // Response: auto-clear session on 401 from protected endpoints
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url    = error?.config?.url || '';
      // Only auto-logout for authenticated backend calls that failed with 401.
      // Skip login/signup/lookup routes so those still surface their own errors.
      const isAuthCall = /\/api\/academy\/(auth\/login|auth\/signup|orientation-portal\/)/.test(url);
      if (status === 401 && !isAuthCall) {
        clearAuth();
        if (error?.response?.data?.blocked && !window.location.pathname.startsWith('/academy/login')) {
          window.location.href = '/academy/login?blocked=1';
        }
      }
      return Promise.reject(error);
    },
  );
};
